// v2 otter — a river otter swimming low in the water with the signature long,
// low undulating body: three humps (chest, arched mid-back, hip) reading as a
// swimmer's wave, a broad flat-muzzled head with a cream chin and throat,
// whisker pads, ear nubs, tucked webbed paws at the sides, and a thick
// two-segment tail trailing with a sideways curl. Two coats: v0 dark river
// brown, v1 lighter cinnamon. Ported 1:1 from age-of-mcp's v2/otter.ts, using
// the library's shared `wetland` (eyes) helper.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const COATS = [
  { body: "#54402e", back: "#5e4836", head: "#67492f", chin: "#cdb89a",
    paw: "#3a2c1e", tail: "#4a3624" },
  { body: "#6e563c", back: "#7a6248", head: "#82603e", chin: "#dcc8a8",
    paw: "#503e2a", tail: "#5e4830" },
];

function otterParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  return [
    // three body humps — the undulating swimmer profile
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.body,
      position: [0.03, 0.024, 0], scale: [1.3, 0.9, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.022, 0), color: c.back,
      position: [-0.005, 0.029, 0], scale: [1.5, 0.85, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.02, 0), color: c.body,
      position: [-0.04, 0.025, 0], scale: [1.2, 0.85, 0.9] },
    // head with the broad flat muzzle
    { geometry: new THREE.IcosahedronGeometry(0.0165, 0), color: c.head,
      position: [0.06, 0.036, 0], scale: [1.2, 0.9, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.chin,
      position: [0.075, 0.031, 0], scale: [1.1, 0.7, 1.15] },
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: "#241a10",
      position: [0.0845, 0.0335, 0] },
    // cream throat running down to the chest
    { geometry: new THREE.IcosahedronGeometry(0.012, 0), color: c.chin,
      position: [0.058, 0.024, 0], scale: [1.2, 0.7, 0.9] },
    // ear nubs
    { geometry: new THREE.IcosahedronGeometry(0.0042, 0), color: c.paw,
      position: [0.052, 0.049, 0.012] },
    { geometry: new THREE.IcosahedronGeometry(0.0042, 0), color: c.paw,
      position: [0.052, 0.049, -0.012] },
    ...wetland.eyes(0.069, 0.041, 0.0095, 0.003),
    // webbed paws tucked at the sides
    { geometry: new THREE.IcosahedronGeometry(0.007, 0), color: c.paw,
      position: [0.038, 0.012, 0.018], scale: [1.2, 0.6, 0.8] },
    { geometry: new THREE.IcosahedronGeometry(0.007, 0), color: c.paw,
      position: [0.038, 0.012, -0.018], scale: [1.2, 0.6, 0.8] },
    { geometry: new THREE.IcosahedronGeometry(0.008, 0), color: c.paw,
      position: [-0.046, 0.012, 0.016], scale: [1.4, 0.5, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.008, 0), color: c.paw,
      position: [-0.046, 0.012, -0.016], scale: [1.4, 0.5, 0.9] },
    // thick two-segment tail trailing with a sideways curl
    { geometry: new THREE.CylinderGeometry(0.0045, 0.0085, 0.045, 5), color: c.tail,
      position: [-0.077, 0.022, 0.004], rotation: [0, 0.15, Math.PI / 2] },
    { geometry: new THREE.ConeGeometry(0.0045, 0.03, 4), color: c.tail,
      position: [-0.108, 0.02, 0.012], rotation: [0, 0.3, Math.PI / 2] },
  ];
}

export const otterV2 = defineModel({
  id: "otter-v2",
  batchId: "fable",
  name: "Otter (v2)",
  tags: ["creature","otter","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly otter.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.08,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => otterParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => otterParts(1) },
  ],
  animations: [],
});
